import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { realmForArea, REALM_LABELS, isPvpRealm } from '../net/index.js';
import { playerLevel, CAVE_UNLOCK_LEVEL } from '@mn/shared';
import { UI, makeButton, makeCloseButton } from '../utils/ui-button.js';

// ── Map — a clean area card + stylized region minimap (mouse/touch-driven). ──
// Surfaces the player's current area name, its realm, and a simple region map that highlights
// where the player is, with a pulsing "you are here" marker. No real-time minimap of the tilemap
// (the world is huge + owned by another worker) — instead a premium, readable region overview.
// Opens over the paused world and resumes it on close (ESC also closes).

/**
 * @typedef MapSceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

/** Realm regions drawn on the stylized map (id → label, color, normalized rect in the map box). */
const REGIONS = [
  { id: 'town', color: 0x3ad0ff, rect: { x: 0.34, y: 0.62, w: 0.32, h: 0.3 } },
  { id: 'wilds', color: 0x34d399, rect: { x: 0.08, y: 0.1, w: 0.5, h: 0.46 } },
  { id: 'badlands', color: 0xff7a7a, rect: { x: 0.6, y: 0.12, w: 0.34, h: 0.42 } },
  { id: 'cave', label: 'THE CAVE', color: 0xa78bfa, rect: { x: 0.08, y: 0.6, w: 0.24, h: 0.3 } },
];

// Tap a region on the map to fast-travel to that realm's main area. Badlands has no overworld map
// yet (PvP arena) → it's shown locked. The Cave is a higher-tier sub-zone gated by player level
// (CAVE_UNLOCK_LEVEL). Keep in sync with net/realm.ts AREA_TO_REALM.
const REALM_TO_AREA = Object.freeze({ town: 'main_1', wilds: 'overworld_1', badlands: null, cave: 'cave_1' });

export class MapScene extends BaseScene {
  /** @type {MapSceneData} */ #sceneData;

  constructor() {
    super({ key: SCENE_KEYS.MAP_SCENE });
  }

  /** @param {MapSceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const { width, height } = this.scale;

    this.add.rectangle(0, 0, width, height, 0x05080d, 0.78).setOrigin(0);

    const panelW = Math.min(720, width - 60);
    const panelH = Math.min(560, height - 60);
    const cx = width / 2;
    const cy = height / 2;
    const top = cy - panelH / 2;
    const left = cx - panelW / 2;

    const panel = this.add.graphics();
    panel.fillStyle(UI.glass, 0.96);
    panel.fillRoundedRect(left, top, panelW, panelH, 18);
    panel.lineStyle(2, UI.emerald, 0.6);
    panel.strokeRoundedRect(left, top, panelW, panelH, 18);

    this.add
      .text(cx, top + 40, 'MAP', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '40px',
        color: UI.emeraldHex,
        stroke: '#05351f',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    makeCloseButton(this, left + panelW - 34, top + 34, () => this.#close());

    const location = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION) || {};
    const area = location.area || 'overworld_1';
    const realm = realmForArea(area);
    const realmLabel = REALM_LABELS[realm] ?? 'The Wilds';
    const areaPretty = this.#prettyArea(area, location.isInterior);

    // ── current area card ──
    const cardX = left + 30;
    const cardY = top + 84;
    const cardW = panelW - 60;
    const cardH = 92;
    const card = this.add.graphics();
    card.fillStyle(0x0d1c14, 0.9);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, 12);
    card.lineStyle(2, UI.gold, 0.7);
    card.strokeRoundedRect(cardX, cardY, cardW, cardH, 12);

    this.add.text(cardX + 18, cardY + 16, '📍 CURRENT AREA', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '14px',
      color: UI.goldHex,
    });
    this.add.text(cardX + 18, cardY + 38, areaPretty, {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '28px',
      color: UI.textHex,
    });
    this.add
      .text(cardX + cardW - 18, cardY + cardH / 2, `${realmLabel}${isPvpRealm(realm) ? '  ⚔ PvP' : ''}`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '20px',
        color: isPvpRealm(realm) ? '#ff9a9a' : UI.emeraldHex,
      })
      .setOrigin(1, 0.5);

    // ── stylized region map ──
    const mapX = left + 30;
    const mapY = cardY + cardH + 20;
    const mapW = panelW - 60;
    const mapH = top + panelH - 70 - mapY;
    this.#drawRegionMap(mapX, mapY, mapW, mapH, realm);

    makeButton(this, {
      x: cx,
      y: top + panelH - 38,
      width: 200,
      height: 46,
      label: 'CLOSE',
      primary: true,
      onClick: () => this.#close(),
    });

    this.cameras.main.fadeIn(160, 0, 0, 0);
  }

  /**
   * Draw the region map: a dark board with each realm as a labeled rounded region; the current
   * realm is highlighted and gets a pulsing "you are here" marker.
   * @param {number} x @param {number} y @param {number} w @param {number} h
   * @param {import('@mn/shared').RealmId} currentRealm
   */
  #drawRegionMap(x, y, w, h, currentRealm) {
    const board = this.add.graphics();
    board.fillStyle(0x081009, 0.95);
    board.fillRoundedRect(x, y, w, h, 12);
    board.lineStyle(2, UI.glassStroke, 1);
    board.strokeRoundedRect(x, y, w, h, 12);

    REGIONS.forEach((region) => {
      const active = region.id === currentRealm;
      const rx = x + region.rect.x * w;
      const ry = y + region.rect.y * h;
      const rw = region.rect.w * w;
      const rh = region.rect.h * h;

      const g = this.add.graphics();
      g.fillStyle(region.color, active ? 0.32 : 0.14);
      g.fillRoundedRect(rx, ry, rw, rh, 10);
      g.lineStyle(active ? 3 : 2, region.color, active ? 1 : 0.5);
      g.strokeRoundedRect(rx, ry, rw, rh, 10);

      this.add
        .text(rx + rw / 2, ry + rh / 2, region.label ?? REALM_LABELS[region.id] ?? region.id, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: active ? '22px' : '18px',
          color: active ? '#ffffff' : UI.subHex,
        })
        .setOrigin(0.5);

      if (active) {
        const marker = this.add.circle(rx + rw / 2, ry + rh - 22, 9, UI.gold, 1).setStrokeStyle(3, 0xffffff, 0.9);
        this.tweens.add({ targets: marker, scale: 1.4, alpha: 0.5, duration: 700, yoyo: true, repeat: -1 });
        this.add
          .text(rx + rw / 2, ry + rh - 4, 'YOU ARE HERE', {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '11px',
            color: UI.goldHex,
          })
          .setOrigin(0.5, 0);
      } else {
        // Non-current realm → tap to fast-travel. Badlands has no map yet (locked). The Cave is
        // level-gated: a "reach Lv. N" cue until the player hits CAVE_UNLOCK_LEVEL.
        const rawArea = REALM_TO_AREA[region.id];
        const lvl = playerLevel(dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_XP) || 0);
        const caveLocked = region.id === 'cave' && lvl < CAVE_UNLOCK_LEVEL;
        const area = caveLocked ? null : rawArea;
        const cue = caveLocked ? `🔒 REACH LV. ${CAVE_UNLOCK_LEVEL}` : rawArea ? '▸ TAP TO TRAVEL' : '🔒 PvP — SOON';
        this.add
          .text(rx + rw / 2, ry + rh - 16, cue, {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '11px',
            color: area ? UI.emeraldHex : '#ff9a9a',
          })
          .setOrigin(0.5, 0);
        const zone = this.add.zone(rx, ry, rw, rh).setOrigin(0).setInteractive({ useHandCursor: !!area });
        zone.on(Phaser.Input.Events.POINTER_OVER, () => g.setAlpha(area ? 1 : 0.85));
        zone.on(Phaser.Input.Events.POINTER_OUT, () => g.setAlpha(1));
        zone.on(Phaser.Input.Events.POINTER_DOWN, () => this.#travelTo(area));
      }
    });
  }

  /**
   * Fast-travel to a realm's main area, or flash a "coming soon" hint when it has no map yet.
   * Resumes + restarts the world scene at the target spawn via WorldScene.travelToArea.
   * @param {string | null} area
   * @returns {void}
   */
  #travelTo(area) {
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    if (prev !== SCENE_KEYS.WORLD_SCENE) return; // only meaningful when the map was opened from the world
    const world = /** @type {import('./world-scene.js').WorldScene} */ (this.scene.get(SCENE_KEYS.WORLD_SCENE));
    if (!area || typeof world?.travelToArea !== 'function') return;
    this.scene.stop(SCENE_KEYS.MAP_SCENE);
    if (this.scene.isPaused(SCENE_KEYS.WORLD_SCENE)) this.scene.resume(SCENE_KEYS.WORLD_SCENE);
    world.travelToArea(area);
  }

  /**
   * @param {string} area
   * @param {boolean} [isInterior]
   * @returns {string}
   */
  #prettyArea(area, isInterior) {
    const base = area
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return isInterior ? `${base} (Interior)` : base;
  }

  update() {
    super.update();
    if (this._controls?.wasBackKeyPressed?.()) {
      this.#close();
    }
  }

  #close() {
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this.scene.stop(SCENE_KEYS.MAP_SCENE);
    if (this.scene.isPaused(prev)) {
      this.scene.resume(prev);
    } else if (this.scene.isSleeping(prev)) {
      // Slept previous scene: wake (not restart) it, otherwise handleSceneResume never fires
      // and the player stays input-locked.
      this.scene.wake(prev);
      this.scene.bringToTop(prev);
    } else if (!this.scene.isActive(prev)) {
      this.scene.start(prev);
    }
    // Always release input — only the resume() path fires handleSceneResume.
    const prevScene = this.scene.get(prev);
    if (prevScene?._controls) prevScene._controls.lockInput = false;
  }
}
